import React, { useState, useEffect, useCallback } from "react";
import { RecurrencyAttributes } from "../../../types";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  Loading,
  ErrorState,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { Icons } from "../../shared/Icons";
import { CreateRecurrencyModal } from "./CreateRecurrencyModal";
import { RecurrenciesListView } from "./RecurrenciesListView";
import { recurrenciesApi } from "../../../services/recurrenciesApi";

export const AdminRecurrencies: React.FC = () => {
  const { company } = useCompany();
  const [recurrencies, setRecurrencies] = useState<RecurrencyAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRecurrency, setEditingRecurrency] =
    useState<RecurrencyAttributes | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recurrencyToDelete, setRecurrencyToDelete] = useState<number | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchRecurrencies = useCallback(async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await recurrenciesApi.getRecurrencies();
      if (response.success && response.data) {
        setRecurrencies(response.data);
      } else {
        setError(response.error || "Falha ao carregar recorrências");
      }
    } catch (err) {
      setError("Falha ao carregar recorrências");
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    if (company?.id) {
      fetchRecurrencies();
    }
  }, [company?.id, fetchRecurrencies]);

  const handleEdit = (recurrency: RecurrencyAttributes) => {
    setEditingRecurrency(recurrency);
    setShowCreateForm(true);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingRecurrency(null);
  };

  const handleRecurrencySaved = () => {
    fetchRecurrencies();
    setShowCreateForm(false);
    setEditingRecurrency(null);
  };

  const handleDelete = (id: number) => {
    setRecurrencyToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!recurrencyToDelete) return;

    setDeleteLoading(true);
    setError(null);

    try {
      const response = await recurrenciesApi.deleteRecurrency(recurrencyToDelete);
      if (response.success) {
        await fetchRecurrencies();
        setShowDeleteModal(false);
        setRecurrencyToDelete(null);
      } else {
        setError(response.error || "Falha ao excluir recorrência");
      }
    } catch (err) {
      setError("Falha ao excluir recorrência");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRecurrencyToDelete(null);
  };

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Repeat sx={{ fontSize: 28 }} />}
        buttonIcon={<Icons.Add sx={{ fontSize: 20 }} />}
        title="Gerenciamento de Recorrências"
        subtitle={
          loading
            ? "Gerencie todas as recorrências de agendamentos"
            : `${recurrencies.length} recorrência${
                recurrencies.length !== 1 ? "s" : ""
              } cadastrada${recurrencies.length !== 1 ? "s" : ""}`
        }
        buttonText="Nova Recorrência"
        onButtonClick={() => setShowCreateForm(true)}
        buttonDisabled={loading}
      />

      {/* Error Message */}
      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => fetchRecurrencies()}
        />
      )}

      <CreateRecurrencyModal
        isOpen={showCreateForm}
        onClose={handleCancel}
        onSuccess={handleRecurrencySaved}
        editingRecurrency={editingRecurrency}
        companyId={company?.id || 0}
      />

      {/* Recurrencies Content */}
      {loading && !showCreateForm ? (
        <Card className="p-4 sm:p-6">
          <Loading message="Carregando recorrências..." />
        </Card>
      ) : recurrencies.length === 0 ? (
        <Card className="p-4 sm:p-6">
          <EmptyState
            icon={<Icons.Repeat sx={{ fontSize: 80 }} />}
            title="Nenhuma recorrência encontrada"
            description="Crie sua primeira recorrência para começar!"
            actionText="Criar Primeira Recorrência"
            onAction={() => setShowCreateForm(true)}
          />
        </Card>
      ) : (
        <RecurrenciesListView
          recurrencies={recurrencies}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Recorrência"
        message="Tem certeza que deseja excluir esta recorrência? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminRecurrencies;
